import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getFragmentData } from 'app/generated/gql'
import { useSidebarCounts } from 'app/hooks/useSidebarCounts'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import { modelData } from 'app/lib/modelData'
import { dueDateSearch } from 'lib/taskCalendar'
import type { DayRange } from 'app/types/task-calendar'
import { dueDateAsDay } from 'app/lib/taskDueDate'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { TaskJson } from 'app/types/entities'
import { bg } from 'lib/background'
import { DELETE_TASK_MUTATION, TASK_FIELDS, TASK_FIELDS_FRAGMENT, UPDATE_TASK_MUTATION } from './graphql'
import type { TaskFieldInput } from './TaskDetailPane'

const PAGE_SIZE = 200
// A month grid holds 42 days; past this many tasks the grid is unreadable anyway.
const MAX_PAGES = 10

async function fetchAllInRange(search: string): Promise<TaskJson[]> {
  const tasks: TaskJson[] = []
  for (let page = 0; page < MAX_PAGES; page++) {
    const { hits, total } = await gqlFetchList<TaskJson>(
      `tasks`,
      { search, pagination: { page, limit: PAGE_SIZE }, sort: { field: `dueDate`, direction: `ASC` } },
      TASK_FIELDS,
    )
    tasks.push(...(hits ?? []))
    if (!hits?.length || tasks.length >= total) break
  }
  return tasks
}

/**
 * A task saved here stays on screen as saved until the search changes or the
 * page reloads, even when a refetch no longer matches it (a task completed on
 * Open Tasks), so it never vanishes mid-pass and index lag cannot flip it back.
 */
export function useCalendarTasks({
  query,
  range,
  refreshTrigger,
  onTaskUpdated,
}: {
  query: string
  range: DayRange
  refreshTrigger?: number
  onTaskUpdated?: () => void
}) {
  const [rawTasks, setRawTasks] = useState<TaskJson[]>([])
  const [undatedCount, setUndatedCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  // The search index lags a delete by a few seconds, so a refetch in that
  // window brings the task back. Same suppression set as TasksTable.
  const [deletedTaskIds, setDeletedTaskIds] = useState<Set<string>>(() => new Set())
  const [savedTasks, setSavedTasks] = useState<Map<string, TaskJson>>(() => new Map())
  const { refresh } = useSidebarCounts()

  const rangeSearch = dueDateSearch(query, range)
  const undatedSearch = dueDateSearch(query, `undated`)
  // Only the newest request may write, so paging quickly never shows an older range.
  const latestRequest = useRef(0)

  const load = () => {
    const request = ++latestRequest.current
    bg(async () => {
      const [inRange, undated] = await Promise.all([
        fetchAllInRange(rangeSearch),
        gqlFetchList(`tasks`, { search: undatedSearch, pagination: { page: 0, limit: 1 } }, `id`),
      ])
      if (request !== latestRequest.current) return
      setRawTasks(inRange)
      setUndatedCount(undated.total ?? 0)
      setIsLoading(false)
    })
  }

  useEffect(() => {
    setIsLoading(true)
    load()
  }, [rangeSearch])

  useEffect(() => {
    setDeletedTaskIds(new Set())
    setSavedTasks(new Map())
  }, [query])

  const mountRefreshTrigger = useRef(refreshTrigger)
  useEffect(() => {
    if (refreshTrigger === mountRefreshTrigger.current) return
    load()
  }, [refreshTrigger])

  const tasks = useMemo(() => {
    const fetchedIds = new Set(rawTasks.map((t) => t.id))
    const keptInRange = [...savedTasks.values()].filter(
      (t) =>
        !fetchedIds.has(t.id) && !!t.dueDate && dueDateAsDay(t.dueDate).isBetween(range.start, range.end, `day`, `[]`),
    )
    return [...rawTasks.map((t) => (t.id && savedTasks.get(t.id)) || t), ...keptInRange].filter(
      (t) => !t.id || !deletedTaskIds.has(t.id),
    )
  }, [rawTasks, savedTasks, deletedTaskIds, rangeSearch])

  const updateTaskFields = async (taskId: string, input: TaskFieldInput): Promise<boolean> => {
    const previous = rawTasks
    const previousSaved = savedTasks
    setRawTasks((prev) =>
      prev.map((t) => (t.id === taskId ? ({ ...t, ...(input as Partial<TaskJson>) } as TaskJson) : t)),
    )
    // A kept task is not in the fetch, so it takes the optimistic patch here.
    setSavedTasks((prev) => {
      const kept = prev.get(taskId)
      return kept ? new Map(prev).set(taskId, { ...kept, ...(input as Partial<TaskJson>) } as TaskJson) : prev
    })
    const { data, error } = await gqlMutation(UPDATE_TASK_MUTATION, { id: taskId, input })
    if (error || !data?.updateTask) {
      setRawTasks(previous)
      setSavedTasks(previousSaved)
      notifyError(`Failed to save${error?.message ? `: ${error.message}` : ``}`)
      return false
    }
    const saved = modelData(getFragmentData(TASK_FIELDS_FRAGMENT, data.updateTask))
    setRawTasks((prev) => prev.map((t) => (t.id === taskId ? saved : t)))
    setSavedTasks((prev) => new Map(prev).set(taskId, saved))
    refresh()
    onTaskUpdated?.()
    return true
  }

  const deleteTask = async (taskId: string): Promise<boolean> => {
    setDeletedTaskIds((prev) => new Set(prev).add(taskId))
    const { data, error } = await gqlMutation(DELETE_TASK_MUTATION, { id: taskId })
    if (error || !data?.deleteTask) {
      setDeletedTaskIds((prev) => {
        const next = new Set(prev)
        next.delete(taskId)
        return next
      })
      notifyError(`Failed to delete${error?.message ? `: ${error.message}` : ``}`)
      return false
    }
    refresh()
    return true
  }

  const toggleStatus = (task: TaskJson) => {
    if (!task.id) return
    const status = task.status === `closed` ? `open` : `closed`
    bg(async () => {
      if (await updateTaskFields(task.id!, { status })) {
        notifySuccess(status === `closed` ? `Task marked complete` : `Task reopened`)
      }
    })
  }

  const reschedule = (task: TaskJson, day: moment.MomentInput) => {
    // Noon local lands on the intended UTC calendar day from UTC-11 to UTC+12,
    // and the server truncates dueDate to that day.
    if (task.id) bg(updateTaskFields(task.id, { dueDate: moment(day).startOf(`day`).add(12, `hours`).toISOString() }))
  }

  return { tasks, undatedCount, isLoading, updateTaskFields, deleteTask, toggleStatus, reschedule }
}
